import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  TextField,
  FormControlLabel,
  Switch,
  Autocomplete,
  Box,
  CircularProgress,
} from "@mui/material";
import debounce from "lodash.debounce";
import { logFieldDefinitions } from "../utils/fieldDefinitionsDebug";

function parseTagsValue(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.map((s) => String(s).trim()).filter(Boolean);
  return String(v)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function tagsToString(arr) {
  return Array.isArray(arr) && arr.length ? arr.join(", ") : "";
}

function normalizeCommitValue(v) {
  if (v == null) return "";
  return String(v).trim();
}

export function getVisibleFieldDefinitions(definitions = []) {
  return (definitions || []).filter((d) => d.visible !== false);
}

/** Autocomplete com busca remota; persiste só ao selecionar ou ao sair do campo (blur). */
function LookupSelectField({
  fieldKey,
  label,
  value,
  onSearchOptions,
  onCommit,
  disabled,
  loadingField,
}) {
  const [inputValue, setInputValue] = useState(value ?? "");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const committedRef = useRef(normalizeCommitValue(value));
  const openRef = useRef(false);

  useEffect(() => {
    const n = normalizeCommitValue(value);
    committedRef.current = n;
    setInputValue(value ?? "");
  }, [value]);

  const runSearch = useMemo(
    () =>
      debounce(async (q) => {
        if (!onSearchOptions) return;
        setSearching(true);
        try {
          const list = await onSearchOptions(fieldKey, q);
          setOptions(Array.isArray(list) ? list : []);
        } catch (_) {
          setOptions([]);
        } finally {
          setSearching(false);
        }
      }, 280),
    [fieldKey, onSearchOptions]
  );

  useEffect(() => {
    return () => runSearch.cancel();
  }, [runSearch]);

  const loadInitial = useCallback(() => {
    runSearch("");
  }, [runSearch]);

  const commitIfChanged = useCallback(
    (nextRaw) => {
      const next = normalizeCommitValue(nextRaw);
      if (next === committedRef.current) return;
      committedRef.current = next;
      onCommit(fieldKey, next === "" ? null : next);
    },
    [fieldKey, onCommit]
  );

  return (
    <Autocomplete
      freeSolo
      fullWidth
      forcePopupIcon
      disabled={disabled}
      loading={searching || loadingField}
      options={options}
      filterOptions={(x) => x}
      inputValue={inputValue}
      onOpen={() => {
        openRef.current = true;
        loadInitial();
      }}
      onClose={() => {
        openRef.current = false;
      }}
      onInputChange={(_, v, reason) => {
        if (reason === "reset") return;
        setInputValue(v);
        if (reason === "input") {
          runSearch(v);
        }
      }}
      onChange={(_, newVal) => {
        const picked =
          typeof newVal === "string" ? newVal : newVal != null ? String(newVal) : "";
        setInputValue(picked);
        commitIfChanged(picked);
      }}
      onBlur={() => {
        if (!openRef.current) {
          commitIfChanged(inputValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          variant="outlined"
          placeholder="Selecione ou digite"
        />
      )}
      slotProps={{
        popper: { sx: { zIndex: 1400 } },
      }}
      ListboxProps={{ style: { maxHeight: 280 } }}
      noOptionsText={searching ? "Buscando…" : "Digite para buscar"}
    />
  );
}

/** Texto/data: edição local; persiste no blur. */
function DraftTextField({
  fieldKey,
  label,
  value,
  type,
  onCommit,
  disabled,
  multiline,
  compact = false,
}) {
  const [draft, setDraft] = useState(value ?? "");
  const committedRef = useRef(normalizeCommitValue(value));

  useEffect(() => {
    const n = normalizeCommitValue(value);
    committedRef.current = n;
    setDraft(value ?? "");
  }, [value]);

  const commit = () => {
    const next = normalizeCommitValue(draft);
    if (next === committedRef.current) return;
    committedRef.current = next;
    onCommit(fieldKey, next === "" ? null : draft);
  };

  const inputVal =
    type === "datetime" || type === "date"
      ? draft
        ? String(draft).replace(" ", "T").slice(0, type === "date" ? 10 : 16)
        : ""
      : draft;

  const isDateType = type === "date" || type === "datetime";

  return (
    <TextField
      label={label}
      variant="outlined"
      type={
        type === "date"
          ? "date"
          : type === "datetime"
          ? "datetime-local"
          : multiline
          ? undefined
          : "text"
      }
      size={compact ? "small" : undefined}
      fullWidth
      multiline={multiline}
      minRows={multiline ? 2 : undefined}
      value={inputVal}
      onChange={(e) => {
        const v = e.target.value;
        if (type === "datetime") {
          setDraft(v ? v.replace("T", " ") + (v.length === 16 ? ":00" : "") : "");
        } else {
          setDraft(v);
        }
      }}
      onBlur={commit}
      disabled={disabled}
      InputLabelProps={isDateType ? { shrink: true } : undefined}
    />
  );
}

/** Um único campo — use dentro de `<Grid item xs={12}>` para o mesmo espaçamento dos demais. */
export function CustomFieldRow({
  definition,
  values = {},
  onCommit,
  onSearchOptions,
  disabled = false,
  loadingFields = {},
}) {
  const def = definition;
  if (!def || def.visible === false) {
    logFieldDefinitions("CustomFieldRow:skip", {
      key: def?.key,
      visible: def?.visible,
      hasDef: !!def,
    });
    return null;
  }

  logFieldDefinitions("CustomFieldRow:render", {
    key: def.key,
    type: def.type,
    label: def.label,
  });

  const key = def.key;
  const val = values[key];
  const label = def.label || key;
  const fieldLoading = !!loadingFields[`cf_${key}`];

  const commit = (k, v) => {
    if (typeof onCommit === "function") onCommit(k, v);
  };

  if (def.type === "boolean") {
    return (
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={Number(val) === 1 || val === true}
            onChange={(e) => commit(key, e.target.checked ? 1 : 0)}
            disabled={disabled || fieldLoading}
          />
        }
        label={label}
      />
    );
  }

  if (def.type === "select") {
    return (
      <LookupSelectField
        fieldKey={key}
        label={label}
        value={val}
        onSearchOptions={onSearchOptions}
        onCommit={commit}
        disabled={disabled || fieldLoading}
        loadingField={fieldLoading}
      />
    );
  }

  if (def.type === "tags" || def.type === "multiselect") {
    const tags = parseTagsValue(val);
    const opts = Array.isArray(def.options) ? def.options : [];
    return (
      <Autocomplete
        multiple
        freeSolo
        fullWidth
        size="small"
        options={opts}
        value={tags}
        onChange={(_, v) => commit(key, tagsToString(v))}
        disabled={disabled || fieldLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            fullWidth
            variant="outlined"
            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          />
        )}
      />
    );
  }

  if (def.type === "date" || def.type === "datetime") {
    return (
      <DraftTextField
        fieldKey={key}
        label={label}
        value={val}
        type={def.type}
        onCommit={commit}
        disabled={disabled || fieldLoading}
        compact
      />
    );
  }

  if (def.type === "number") {
    return (
      <DraftTextField
        fieldKey={key}
        label={label}
        value={val}
        type="text"
        onCommit={(k, v) => commit(k, v === "" || v == null ? null : Number(v) || v)}
        disabled={disabled || fieldLoading}
        compact
      />
    );
  }

  if (def.type === "textarea") {
    return (
      <DraftTextField
        fieldKey={key}
        label={label}
        value={val}
        multiline
        onCommit={commit}
        disabled={disabled || fieldLoading}
        compact
      />
    );
  }

  return (
    <DraftTextField
      fieldKey={key}
      label={label}
      value={val}
      onCommit={commit}
      disabled={disabled || fieldLoading}
      compact
    />
  );
}

/**
 * Campo da solicitação no atendimento — mesmo padrão visual dos Select/TextField do painel (tamanho médio).
 */
export function RequestFieldInput({
  definition,
  value,
  onCommit,
  onSearchOptions,
  disabled = false,
  loadingField = false,
}) {
  const def = definition;
  if (!def?.key) return null;

  const key = def.key;
  const label = def.label || key;
  const fieldType = def.type || def.field_type || "text";
  const commit = (v) => {
    if (typeof onCommit === "function") onCommit(key, v);
  };

  if (fieldType === "select") {
    if (typeof onSearchOptions === "function") {
      return (
        <LookupSelectField
          fieldKey={key}
          label={label}
          value={value}
          onSearchOptions={onSearchOptions}
          onCommit={(_, v) => commit(v)}
          disabled={disabled}
          loadingField={loadingField}
        />
      );
    }
    return (
      <TextField
        label={label}
        fullWidth
        variant="outlined"
        value={value ?? ""}
        onChange={(e) => commit(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        disabled={disabled}
        placeholder="Selecione ou digite"
      />
    );
  }

  if (fieldType === "date" || fieldType === "datetime") {
    return (
      <Box sx={{ width: "100%", minHeight: 56 }}>
        <DraftTextField
          fieldKey={key}
          label={label}
          value={value}
          type={fieldType}
          onCommit={(_, v) => commit(v)}
          disabled={disabled}
        />
      </Box>
    );
  }

  return (
    <DraftTextField
      fieldKey={key}
      label={label}
      value={value}
      type={fieldType === "number" ? "text" : "text"}
      multiline={fieldType === "textarea"}
      onCommit={(_, v) => commit(v)}
      disabled={disabled}
    />
  );
}

/**
 * Campos legado + EAV (bloco único). Para atendimento, prefira um Grid item por campo via CustomFieldRow.
 */
export default function CustomFieldsBlock({
  definitions = [],
  values = {},
  onCommit,
  onSearchOptions,
  loading = false,
  disabled = false,
  loadingFields = {},
}) {
  const visibleDefs = useMemo(
    () => getVisibleFieldDefinitions(definitions),
    [definitions]
  );

  if (!visibleDefs.length) return null;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      {visibleDefs.map((def) => (
        <CustomFieldRow
          key={def.key}
          definition={def}
          values={values}
          onCommit={onCommit}
          onSearchOptions={onSearchOptions}
          disabled={disabled}
          loadingFields={loadingFields}
        />
      ))}
    </Box>
  );
}
